import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  LayoutDashboard, 
  MessageCircle, 
  Mail, 
  FileText, 
  Calendar, 
  MessageSquare,
  Download,
  Settings,
  TrendingUp,
  Users,
  Clock,
  CheckCircle,
  LogOut,
  User,
  Shield
} from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { db } from '../firebase'
import { collection, query, where, getDocs, onSnapshot, Timestamp, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore'
import { useToast } from '../context/ToastContext'
import { useNotifications } from '../hooks/useNotifications'

// Import dashboard sections
import ChatSection from '../components/dashboard/ChatSection'
import StatsSection from '../components/dashboard/StatsSection'
import ContactSection from '../components/dashboard/ContactSection'
import ApplicationsSection from '../components/dashboard/ApplicationsSection'
import ConsultationsSection from '../components/dashboard/ConsultationsSection'
import CalendarView from '../components/dashboard/CalendarView'
import QuickRepliesSection from '../components/dashboard/QuickRepliesSection'
import ExportSection from '../components/dashboard/ExportSection'
import ReviewsSection from '../components/dashboard/ReviewsSection'
import BlogCMS from '../components/dashboard/BlogCMS'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  
  // Use notification hook
  useNotifications(showToast)
  
  const [staffMember, setStaffMember] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [stats, setStats] = useState({
    activeChats: 0,
    todayMessages: 0,
    contactForms: 0,
    applications: 0,
    consultations: 0,
    visitors: 0
  })
  const audioRef = useRef(null)
  const notifiedSessions = useRef(new Set())

  // Initialize notification sound
  useEffect(() => {
    audioRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBzKM0fPTgjMGHm7A7+OZURE')
    audioRef.current.volume = 0.5
  }, [])

  // Global notification listener for handoff requests
  useEffect(() => {
    if (!staffMember) return

    const sessionsRef = collection(db, 'chatSessions')
    const q = query(sessionsRef, where('status', '==', 'active'))

    const unsubscribe = onSnapshot(q, (snapshot) => {
      snapshot.docs.forEach(doc => {
        const session = {
          id: doc.id,
          ...doc.data()
        }
        
        // Check for new handoff requests
        if (session.handoffRequested && !session.assignedToStaff && !notifiedSessions.current.has(session.id)) {
          // Play notification sound
          audioRef.current?.play().catch(e => console.log('Audio play failed:', e))
          
          // Show toast notification
          showToast(`🔔 ${session.userName} kërkon të flasë me stafin!`, 'info')
          
          // Mark as notified
          notifiedSessions.current.add(session.id)
        }
      })
    })

    return () => unsubscribe()
  }, [staffMember, showToast])

  // Check authentication
  useEffect(() => {
    const checkAuth = async () => {
      // Show loading screen for 3 seconds
      await new Promise(resolve => setTimeout(resolve, 3000))
      
      const staffData = localStorage.getItem('staffMember')
      if (!staffData) {
        navigate('/staff-login')
        return
      }
      setStaffMember(JSON.parse(staffData))
      setLoading(false)
    }
    
    checkAuth()
  }, [navigate])

  // Update online status
  useEffect(() => {
    if (!staffMember || !staffMember.id) return

    const updateOnlineStatus = async (isOnline) => {
      try {
        const staffRef = doc(db, 'staff', staffMember.id)
        await updateDoc(staffRef, { isOnline })
      } catch (error) {
        console.error('Error updating online status:', error)
      }
    }

    // Set online when component mounts
    updateOnlineStatus(true)

    // Set offline when component unmounts or user closes tab
    const handleBeforeUnload = () => updateOnlineStatus(false)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      updateOnlineStatus(false)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [staffMember])

  // Load statistics - REAL DATA
  useEffect(() => {
    if (!staffMember) return

    const loadStats = async () => {
      try {
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Real-time listeners for accurate data
        const unsubscribers = []

        // Active chats - REAL TIME
        const sessionsUnsub = onSnapshot(
          query(collection(db, 'chatSessions'), where('status', '==', 'active')),
          (snapshot) => {
            setStats(prev => ({ ...prev, activeChats: snapshot.size }))
          }
        )
        unsubscribers.push(sessionsUnsub)

        // Today's messages - REAL TIME
        const messagesUnsub = onSnapshot(
          query(collection(db, 'chatMessages')),
          (snapshot) => {
            const todayMsgs = snapshot.docs.filter(doc => {
              const msgDate = doc.data().timestamp?.toDate()
              return msgDate && msgDate >= today
            })
            setStats(prev => ({ ...prev, todayMessages: todayMsgs.length }))
          }
        )
        unsubscribers.push(messagesUnsub)

        // Contact forms - REAL TIME
        const contactsUnsub = onSnapshot(
          collection(db, 'contactSubmissions'),
          (snapshot) => {
            setStats(prev => ({ ...prev, contactForms: snapshot.size }))
          }
        )
        unsubscribers.push(contactsUnsub)

        // Applications - REAL TIME
        const appsUnsub = onSnapshot(
          collection(db, 'projectApplications'),
          (snapshot) => {
            setStats(prev => ({ ...prev, applications: snapshot.size }))
          }
        )
        unsubscribers.push(appsUnsub)

        // Consultations - REAL TIME
        const consUnsub = onSnapshot(
          collection(db, 'consultationRequests'),
          (snapshot) => {
            setStats(prev => ({ ...prev, consultations: snapshot.size }))
          }
        )
        unsubscribers.push(consUnsub)

        return () => {
          unsubscribers.forEach(unsub => unsub())
        }
      } catch (error) {
        console.error('Error loading stats:', error)
      }
    }

    loadStats()
  }, [staffMember])

  const handleLogout = async () => {
    try {
      // Transfer all assigned chats back to bot
      const sessionsRef = collection(db, 'chatSessions')
      const q = query(
        sessionsRef,
        where('assignedToStaff', '==', staffMember.id),
        where('status', '==', 'active')
      )
      const snapshot = await getDocs(q)

      // Update all sessions assigned to this staff member
      const updatePromises = snapshot.docs.map(async (docSnapshot) => {
        const session = docSnapshot.data()
        
        // Update session to bot
        await updateDoc(doc(db, 'chatSessions', docSnapshot.id), {
          assignedTo: 'bot',
          assignedToStaff: null,
          assignedToStaffName: null,
          handoffRequested: false
        })

        // Send bot message to notify client
        await addDoc(collection(db, 'chatMessages'), {
          sessionId: session.sessionId,
          sender: 'bot',
          senderName: 'AI Asistent',
          text: `${staffMember.name} doli nga linja. Po ju ndihmoj unë përsëri! Si mund t'ju ndihmoj?`,
          timestamp: serverTimestamp()
        })
      })

      await Promise.all(updatePromises)
    } catch (error) {
      console.error('Error transferring chats to bot:', error)
    }

    localStorage.removeItem('staffMember')
    navigate('/staff-login')
  }

  const getInitials = (name) => {
    if (!name) return 'U'
    const parts = name.split(' ')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return name.substring(0, 2).toUpperCase()
  }

  const tabs = [
    { id: 'overview', label: 'Statistika', icon: LayoutDashboard },
    { id: 'chat', label: 'Live Chat', icon: MessageCircle },
    { id: 'contacts', label: 'Kontaktet', icon: Mail },
    { id: 'applications', label: 'Aplikacionet', icon: FileText },
    { id: 'consultations', label: 'Konsultimet', icon: Calendar },
    { id: 'reviews', label: 'Recensionet', icon: MessageSquare },
    { id: 'blog', label: 'Blog CMS', icon: FileText },
    { id: 'replies', label: 'Quick Replies', icon: MessageSquare },
    { id: 'export', label: 'Eksporto', icon: Download },
  ]

  if (loading || !staffMember) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-dark via-bronze-900 to-dark flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="inline-flex items-center justify-center w-24 h-24 bg-gradient-gold rounded-full mb-6 shadow-glow"
          >
            <Shield className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-3xl font-display font-bold text-gradient-gold mb-2"
          >
            Admin Dashboard
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="text-beige-300"
          >
            Po verifikohet qasja...
          </motion.p>
        </motion.div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
      </Helmet>
      
      <div className="min-h-screen bg-dark">
        {/* Top Navigation */}
        <div className="bg-bronze-900/30 backdrop-blur-sm border-b border-gold-500/20 sticky top-0 z-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-gold rounded-lg flex items-center justify-center">
                  <LayoutDashboard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-display font-bold text-gradient-gold">
                    Admin Dashboard
                  </h1>
                  <p className="text-xs text-beige-400">Contenta Marketing</p>
                </div>
              </div>
              
              {/* Staff Profile */}
              <div className="flex items-center space-x-2 lg:space-x-4">
                <div className="hidden md:block text-right">
                  <p className="text-sm font-medium text-cream">{staffMember.name}</p>
                  <p className="text-xs text-beige-400">{staffMember.email}</p>
                </div>
                <div className="w-10 h-10 bg-gradient-gold rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {getInitials(staffMember.name)}
                </div>
                <button
                  onClick={handleLogout}
                  className="btn-secondary text-sm flex items-center space-x-1 lg:space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Dil</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Sidebar */}
            <div className="w-full lg:w-64 space-y-2">
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 lg:w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-gradient-gold text-white shadow-gold'
                          : 'bg-bronze-900/30 text-beige-200 hover:bg-bronze-900/50'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1">
              {activeTab === 'overview' && <StatsSection stats={stats} onTabChange={setActiveTab} />}
              {activeTab === 'chat' && <ChatSection staffMember={staffMember} />}
              {activeTab === 'contacts' && <ContactSection />}
              {activeTab === 'applications' && <ApplicationsSection />}
              {activeTab === 'consultations' && <CalendarView />}
              {activeTab === 'reviews' && <ReviewsSection />}
              {activeTab === 'blog' && <BlogCMS />}
              {activeTab === 'replies' && <QuickRepliesSection />}
              {activeTab === 'export' && <ExportSection />}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default AdminDashboard
